import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { ReactComponent as ArrowLeftIcon } from 'assets/icons/navigation/small/arrow_left_sm.svg';
import { ReactComponent as CheckIcon } from 'assets/icons/green-circle-white-check.svg';
import { ReactComponent as CheckGIcon } from 'assets/icons/check-g.svg';
import { ReactComponent as XIConRIcon } from 'assets/icons/x-icon-r.svg';
import { ReactComponent as ExclamationOIcon } from 'assets/icons/exclamation-o.svg';
import { ReactComponent as ExclamationBIcon } from 'assets/icons/exclamation-b.svg';
import { ReactComponent as HourglassOIcon } from 'assets/icons/hourglass-o.svg';
import { format, parseISO } from 'date-fns';
import { AgendaTarefa } from 'services/reports/endpoints/AgendaService';
import { DatePicker as _DatePicker } from 'newComponents/Form/DatePicker';
import { TimePicker } from 'newComponents/Form/TimePicker';
import { MultiSelectDropdown, FilterOption } from 'newComponents/Form/Multiselect';
import { FormGroup as _FormGroup } from 'components/Form/subcomponents/FormGroup';
import { useAgendaUsers } from './useAgendaUsers';

interface DatePickerProps {
  value?: string;
  onChange: (val: Date | string | '') => void;
  disabled?: boolean;
  placeholder?: string;
  width?: string;
  usePortal?: boolean;
}
const DatePicker = _DatePicker as unknown as React.FC<DatePickerProps>;
const FormGroup = _FormGroup as unknown as React.FC<{ children: React.ReactNode; label?: string }>;
import { ReactComponent as ChevronUpIcon } from 'assets/icons/navigation/small/chevron-right-sm.svg';
import {
  ActivityListWrapper,
  Table,
  EditBulkThead,
  EditTr,
  EditCheckboxCell,
  EditMetaCell,
  EditInputCell,
  EditActionsCell,
  EditActionsCellContent,
  EditBulkMeta,
  EditBulkCount,
  EditBulkLink,
  EditSaveButton,
  KebabButton,
  RowMenuWrapper,
  PortalRowMenu,
  RowMenuItem,
  DesktopOnly,
  MobileEditWrapper,
  MobileEditBulkHeader,
  MobileEditBulkTopRow,
  MobileEditBulkCount,
  MobileEditBulkLinkRow,
  MobileEditFieldRow,
  MobileEditCard,
  MobileEditCardTopRow,
  MobileEditCardActions,
  MobileEditCardTitle,
  MobileEditCardTitleBold,
  MobileEditCardFields,
  ExpandButton,
  StatusChip,
  OrangeCheckbox,
  MobileEditBottomBar,
} from './styled';

export interface RowEdit {
  date: string;
  time: string;
  executorIds: string[];
}

interface EditActivityListProps {
  tarefas: AgendaTarefa[];
  isSaving: boolean;
  canDelete: boolean;
  onSave: (edits: Record<number, RowEdit>) => void;
  onRequestExcluir: (tarefa: AgendaTarefa) => void;
  onBulkExcluir: (tarefas: AgendaTarefa[]) => void;
}

const KebabIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <circle cx="8" cy="3" r="1.5" />
    <circle cx="8" cy="8" r="1.5" />
    <circle cx="8" cy="13" r="1.5" />
  </svg>
);

const buildAvatarUrl = (fotoPath: string | null): string => {
  if (!fotoPath) return '';
  const base = (process.env.REACT_APP_URL_IMAGES || '').replace(/\/$/, '');
  const path = fotoPath.startsWith('/') ? fotoPath : `/${fotoPath}`;
  return `${base}${path}`;
};

const toDateStr = (iso: string | null): string => {
  if (!iso) return '';
  try {
    return format(parseISO(iso), 'yyyy-MM-dd');
  } catch {
    return '';
  }
};

const toTimeStr = (iso: string | null): string => {
  if (!iso) return '';
  try {
    return format(parseISO(iso), 'HH:mm');
  } catch {
    return '';
  }
};

const toStoredDate = (val: Date | string | ''): string => {
  if (!val) return '';
  try {
    if (val instanceof Date) return format(val, 'yyyy-MM-dd');
    return format(parseISO(val as string), 'yyyy-MM-dd');
  } catch {
    return '';
  }
};

const getStatusVariant = (tarefa: AgendaTarefa): 'green' | 'blue' | 'red' => {
  switch (tarefa.status) {
    case 3:
    case 5:
      return 'green';
    case 4:
      return 'red';
    case 2:
      return 'blue';
    default: // status 1 (Pendente)
      try {
        if (tarefa.dataPrevista && parseISO(tarefa.dataPrevista) < new Date()) return 'red';
      } catch {}
      return 'blue';
  }
};

const getStatusLabel = (tarefa: AgendaTarefa): string => {
  switch (tarefa.status) {
    case 2:
      return 'Aguardando aprovação';
    case 3:
      return 'Realizada';
    case 4:
      return 'Não realizada';
    case 5:
      return 'Baixada automaticamente';
    default: // status 1 (Pendente)
      try {
        if (tarefa.dataPrevista && parseISO(tarefa.dataPrevista) < new Date()) return 'Atrasada';
      } catch {}
      return 'Agendada';
  }
};

const getStatusIcon = (tarefa: AgendaTarefa): React.ReactElement => {
  switch (tarefa.status) {
    case 3:
      return <CheckGIcon width={20} height={20} style={{ flexShrink: 0 }} />;
    case 4:
      return <XIConRIcon width={20} height={20} style={{ flexShrink: 0 }} />;
    case 5:
      return <CheckIcon width={20} height={20} style={{ flexShrink: 0 }} />;
    case 2:
      return <HourglassOIcon width={20} height={20} style={{ flexShrink: 0 }} />;
    default: // status 1 (Pendente)
      try {
        if (tarefa.dataPrevista && parseISO(tarefa.dataPrevista) < new Date())
          return <ExclamationOIcon width={20} height={20} style={{ flexShrink: 0 }} />;
      } catch {}
      return <ExclamationBIcon width={20} height={20} style={{ flexShrink: 0 }} />;
  }
};

const buildExecutorOptions = (tarefa: AgendaTarefa): FilterOption[] => {
  return tarefa.executores
    .map(exec => ({
      label: exec.nome,
      value: String(exec.executorId),
      foto: buildAvatarUrl(exec.fotoPath) || undefined,
    }))
    .filter(opt => opt.value !== '' && opt.value !== '0');
};

export const EditActivityList: React.FC<EditActivityListProps> = ({
  tarefas,
  isSaving,
  canDelete,
  onSave,
  onRequestExcluir,
  onBulkExcluir,
}) => {
  const allIds = tarefas.map(t => t.numero);

  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set(allIds));
  const [rowEdits, setRowEdits] = useState<Record<number, RowEdit>>(() => {
    const init: Record<number, RowEdit> = {};
    tarefas.forEach(t => {
      init[t.numero] = {
        date: toDateStr(t.dataPrevista),
        time: toTimeStr(t.dataPrevista),
        executorIds: t.executores.map(e => String(e.executorId)).filter(id => id && id !== '0'),
      };
    });
    return init;
  });

  const [bulkDate, setBulkDate] = useState('');
  const [bulkTime, setBulkTime] = useState('');
  const [bulkExecutorIds, setBulkExecutorIds] = useState<string[]>([]);
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);
  type MenuPos = { top?: number; bottom?: number; right: number };
  const [menuPos, setMenuPos] = useState<MenuPos | null>(null);
  const portalMenuRef = useRef<HTMLUListElement>(null);

  const closeMenu = () => {
    setOpenMenuId(null);
    setMenuPos(null);
  };

  const handleKebabClick = (e: React.MouseEvent<HTMLButtonElement>, id: number) => {
    if (openMenuId === id) {
      closeMenu();
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const vw = document.documentElement.clientWidth;
    const vh = document.documentElement.clientHeight;
    const upward = vh - rect.bottom < 120;
    setMenuPos(
      upward ? { bottom: vh - rect.top, right: vw - rect.right } : { top: rect.bottom, right: vw - rect.right },
    );
    setOpenMenuId(id);
  };

  useEffect(() => {
    if (openMenuId === null) return;
    const handleOutside = (e: MouseEvent) => {
      if (portalMenuRef.current && !portalMenuRef.current.contains(e.target as Node)) closeMenu();
    };
    const handleScroll = () => closeMenu();
    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('scroll', handleScroll, true);
    };
  }, [openMenuId]); // eslint-disable-line react-hooks/exhaustive-deps
  const { options: userOptions, search: searchUsers } = useAgendaUsers();

  const selectAllRef = useRef<HTMLInputElement>(null);
  const allSelected = selectedIds.size === tarefas.length;
  const someSelected = selectedIds.size > 0 && selectedIds.size < tarefas.length;
  const noneSelected = selectedIds.size === 0;

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = someSelected;
    }
  }, [someSelected]);

  const handleSelectAll = () => {
    setSelectedIds(allSelected ? new Set() : new Set(allIds));
  };

  const handleSelectRow = (id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const applyBulkDate = (val: Date | string | '') => {
    const dateStr = toStoredDate(val);
    if (dateStr === bulkDate) return;
    setBulkDate(dateStr);
    setRowEdits(prev => {
      const next = { ...prev };
      selectedIds.forEach(id => {
        next[id] = { ...next[id], date: dateStr };
      });
      return next;
    });
  };

  const applyBulkTime = (val: string) => {
    if (val === bulkTime) return;
    setBulkTime(val);
    setRowEdits(prev => {
      const next = { ...prev };
      selectedIds.forEach(id => {
        next[id] = { ...next[id], time: val };
      });
      return next;
    });
  };

  const updateDate = (id: number, val: Date | string | '') => {
    const dateStr = toStoredDate(val);
    setRowEdits(prev => {
      if (prev[id]?.date === dateStr) return prev;
      return { ...prev, [id]: { ...prev[id], date: dateStr } };
    });
  };

  const updateTime = (id: number, val: string) => {
    setRowEdits(prev => ({ ...prev, [id]: { ...prev[id], time: val } }));
  };

  const updateExecutors = (id: number, vals: string[]) => {
    setRowEdits(prev => ({ ...prev, [id]: { ...prev[id], executorIds: vals } }));
  };

  const applyBulkExecutors = (vals: string[]) => {
    setBulkExecutorIds(vals);
    setRowEdits(prev => {
      const next = { ...prev };
      selectedIds.forEach(id => {
        next[id] = { ...next[id], executorIds: vals };
      });
      return next;
    });
  };

  const handleSave = () => {
    const selectedEdits = Object.fromEntries(Object.entries(rowEdits).filter(([k]) => selectedIds.has(Number(k))));
    onSave(selectedEdits);
  };

  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());
  const toggleExpand = (id: number) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const count = selectedIds.size;
  const label = `${count} atividade${count !== 1 ? 's' : ''} selecionada${count !== 1 ? 's' : ''}`;

  return (
    <ActivityListWrapper>
      {/* ── Desktop ── */}
      <DesktopOnly>
        <Table>
          <EditBulkThead>
            <tr>
              <EditCheckboxCell as="th">
                <OrangeCheckbox ref={selectAllRef} checked={allSelected} onChange={handleSelectAll} />
              </EditCheckboxCell>
              <EditMetaCell as="th">
                <EditBulkMeta>
                  <EditBulkCount>{label}</EditBulkCount>
                  <EditBulkLink>
                    Edite tudo de uma vez <ArrowLeftIcon style={{ transform: 'scaleX(-1)', verticalAlign: 'middle' }} />
                  </EditBulkLink>
                </EditBulkMeta>
              </EditMetaCell>
              <EditInputCell as="th">
                <FormGroup label="Data">
                  <DatePicker
                    value={bulkDate}
                    onChange={applyBulkDate}
                    disabled={noneSelected}
                    placeholder=""
                    width="140px"
                    usePortal
                  />
                </FormGroup>
              </EditInputCell>
              <EditInputCell as="th">
                <FormGroup label="Horário">
                  <TimePicker
                    value={bulkTime}
                    onChange={applyBulkTime}
                    disabled={noneSelected}
                    placeholder=""
                    width="110px"
                    usePortal
                  />
                </FormGroup>
              </EditInputCell>
              <EditInputCell as="th">
                <FormGroup label="Executores">
                  <MultiSelectDropdown
                    options={userOptions}
                    value={bulkExecutorIds}
                    onChange={applyBulkExecutors}
                    placeholder="Buscar executor..."
                    disabled={noneSelected}
                    width="170px"
                    hasFoto
                    searchable
                    searchMode="remote"
                    onSearch={searchUsers}
                    usePortal
                  />
                </FormGroup>
              </EditInputCell>
              <EditActionsCell as="th">
                <EditActionsCellContent>
                  {canDelete && (
                    <RowMenuWrapper>
                      <KebabButton
                        type="button"
                        aria-label="Ações em lote"
                        onClick={(e: React.MouseEvent<HTMLButtonElement>) => handleKebabClick(e, -1)}
                      >
                        <KebabIcon />
                      </KebabButton>
                      {openMenuId === -1 &&
                        menuPos &&
                        ReactDOM.createPortal(
                          <PortalRowMenu ref={portalMenuRef} style={menuPos}>
                            <RowMenuItem
                              danger
                              onClick={() => {
                                closeMenu();
                                onBulkExcluir(tarefas.filter(t => selectedIds.has(t.numero)));
                              }}
                            >
                              Excluir selecionados
                            </RowMenuItem>
                          </PortalRowMenu>,
                          document.body,
                        )}
                    </RowMenuWrapper>
                  )}
                  <EditSaveButton
                    type="button"
                    data-testid="btn-salvar-edicao"
                    onClick={handleSave}
                    disabled={isSaving}
                  >
                    {isSaving ? 'Salvando...' : 'Aplicar alterações'}
                  </EditSaveButton>
                </EditActionsCellContent>
              </EditActionsCell>
            </tr>
          </EditBulkThead>
          <tbody>
            {tarefas.map((tarefa, index) => {
              const selected = selectedIds.has(tarefa.numero);
              const edit = rowEdits[tarefa.numero];
              const executorOptions = buildExecutorOptions(tarefa);
              return (
                <EditTr
                  key={tarefa.numero}
                  selected={selected}
                  style={{ position: 'relative', zIndex: tarefas.length - index }}
                >
                  <EditCheckboxCell>
                    <OrangeCheckbox checked={selected} onChange={() => handleSelectRow(tarefa.numero)} />
                  </EditCheckboxCell>
                  <EditMetaCell>
                    <strong>Nº {tarefa.numero}:</strong> {tarefa.descricao}
                  </EditMetaCell>
                  <EditInputCell>
                    <FormGroup>
                      <DatePicker
                        value={edit?.date ?? ''}
                        onChange={val => updateDate(tarefa.numero, val)}
                        placeholder="Data"
                        width="140px"
                        usePortal
                      />
                    </FormGroup>
                  </EditInputCell>
                  <EditInputCell>
                    <FormGroup>
                      <TimePicker
                        value={edit?.time ?? ''}
                        onChange={val => updateTime(tarefa.numero, val)}
                        placeholder="Horário"
                        width="110px"
                        usePortal
                      />
                    </FormGroup>
                  </EditInputCell>
                  <EditInputCell>
                    <FormGroup>
                      <MultiSelectDropdown
                        options={[
                          ...executorOptions,
                          ...userOptions.filter(u => !executorOptions.some(e => e.value === u.value)),
                        ]}
                        value={edit?.executorIds ?? []}
                        onChange={vals => updateExecutors(tarefa.numero, vals)}
                        placeholder="Buscar executor..."
                        hasFoto
                        searchable
                        searchMode="remote"
                        onSearch={searchUsers}
                        width="170px"
                        usePortal
                      />
                    </FormGroup>
                  </EditInputCell>
                  <EditActionsCell>
                    {canDelete && (
                      <RowMenuWrapper>
                        <KebabButton
                          type="button"
                          aria-label="Ações"
                          onClick={(e: React.MouseEvent<HTMLButtonElement>) => handleKebabClick(e, tarefa.numero)}
                        >
                          <KebabIcon />
                        </KebabButton>
                        {openMenuId === tarefa.numero &&
                          menuPos &&
                          ReactDOM.createPortal(
                            <PortalRowMenu ref={portalMenuRef} style={menuPos}>
                              <RowMenuItem
                                danger
                                onClick={() => {
                                  closeMenu();
                                  onRequestExcluir(tarefa);
                                }}
                              >
                                Excluir
                              </RowMenuItem>
                            </PortalRowMenu>,
                            document.body,
                          )}
                      </RowMenuWrapper>
                    )}
                  </EditActionsCell>
                </EditTr>
              );
            })}
          </tbody>
        </Table>
      </DesktopOnly>

      {/* ── Mobile ── */}
      <MobileEditWrapper>
        <MobileEditBulkHeader>
          <MobileEditBulkTopRow>
            <OrangeCheckbox ref={selectAllRef} checked={allSelected} onChange={handleSelectAll} />
            <MobileEditBulkCount>{label}</MobileEditBulkCount>
          </MobileEditBulkTopRow>
          <MobileEditBulkLinkRow>
            <ArrowLeftIcon style={{ transform: 'rotate(-90deg)', color: '#7f746a' }} />
            <EditBulkLink>Edite tudo de uma vez</EditBulkLink>
          </MobileEditBulkLinkRow>
          <MobileEditFieldRow>
            <div style={{ width: '140px', flexShrink: 0 }}>
              <FormGroup label="Data">
                <DatePicker
                  value={bulkDate}
                  onChange={applyBulkDate}
                  disabled={noneSelected}
                  placeholder=""
                  width="140px"
                  usePortal
                />
              </FormGroup>
            </div>
            <div style={{ width: '110px', flexShrink: 0 }}>
              <FormGroup label="Horário">
                <TimePicker
                  value={bulkTime}
                  onChange={applyBulkTime}
                  disabled={noneSelected}
                  placeholder=""
                  width="110px"
                  usePortal
                />
              </FormGroup>
            </div>
          </MobileEditFieldRow>
          <FormGroup label="Executor">
            <MultiSelectDropdown
              options={userOptions}
              value={bulkExecutorIds}
              onChange={applyBulkExecutors}
              placeholder="Buscar executor..."
              disabled={noneSelected}
              width="100%"
              hasFoto
              searchable
              searchMode="remote"
              onSearch={searchUsers}
              usePortal
            />
          </FormGroup>
        </MobileEditBulkHeader>

        {tarefas.map(tarefa => {
          const selected = selectedIds.has(tarefa.numero);
          const edit = rowEdits[tarefa.numero];
          const executorOptions = buildExecutorOptions(tarefa);
          const isExpanded = expandedIds.has(tarefa.numero);
          const variant = getStatusVariant(tarefa);
          return (
            <MobileEditCard key={tarefa.numero} selected={selected}>
              <MobileEditCardTopRow>
                <OrangeCheckbox checked={selected} onChange={() => handleSelectRow(tarefa.numero)} />
                <StatusChip variant={variant}>
                  {getStatusIcon(tarefa)}
                  {getStatusLabel(tarefa)}
                </StatusChip>
                <MobileEditCardActions>
                  {canDelete && (
                    <RowMenuWrapper>
                      <KebabButton
                        type="button"
                        aria-label="Ações"
                        onClick={(e: React.MouseEvent<HTMLButtonElement>) => handleKebabClick(e, tarefa.numero)}
                      >
                        <KebabIcon />
                      </KebabButton>
                      {openMenuId === tarefa.numero &&
                        menuPos &&
                        ReactDOM.createPortal(
                          <PortalRowMenu ref={portalMenuRef} style={menuPos}>
                            <RowMenuItem
                              danger
                              onClick={() => {
                                closeMenu();
                                onRequestExcluir(tarefa);
                              }}
                            >
                              Excluir
                            </RowMenuItem>
                          </PortalRowMenu>,
                          document.body,
                        )}
                    </RowMenuWrapper>
                  )}
                  <ExpandButton
                    type="button"
                    onClick={() => toggleExpand(tarefa.numero)}
                    aria-label={isExpanded ? 'Recolher' : 'Expandir'}
                  >
                    <ChevronUpIcon
                      style={{
                        transform: isExpanded ? 'rotate(-90deg)' : 'rotate(90deg)',
                        transition: 'transform 0.2s',
                      }}
                    />
                  </ExpandButton>
                </MobileEditCardActions>
              </MobileEditCardTopRow>

              <MobileEditCardTitle>
                <MobileEditCardTitleBold>Nº {tarefa.numero}:</MobileEditCardTitleBold> {tarefa.descricao}
              </MobileEditCardTitle>

              {isExpanded && (
                <MobileEditCardFields>
                  <MobileEditFieldRow>
                    <div style={{ width: '140px', flexShrink: 0 }}>
                      <FormGroup label="Data">
                        <DatePicker
                          value={edit?.date ?? ''}
                          onChange={val => updateDate(tarefa.numero, val)}
                          placeholder="Data"
                          width="140px"
                          usePortal
                        />
                      </FormGroup>
                    </div>
                    <div style={{ width: '110px', flexShrink: 0 }}>
                      <FormGroup label="Horário">
                        <TimePicker
                          value={edit?.time ?? ''}
                          onChange={val => updateTime(tarefa.numero, val)}
                          placeholder="Horário"
                          width="110px"
                          usePortal
                        />
                      </FormGroup>
                    </div>
                  </MobileEditFieldRow>
                  <FormGroup label="Executor">
                    <MultiSelectDropdown
                      options={[
                        ...executorOptions,
                        ...userOptions.filter(u => !executorOptions.some(e => e.value === u.value)),
                      ]}
                      value={edit?.executorIds ?? []}
                      onChange={vals => updateExecutors(tarefa.numero, vals)}
                      placeholder="Buscar executor..."
                      hasFoto
                      searchable
                      searchMode="remote"
                      onSearch={searchUsers}
                      width="100%"
                      usePortal
                    />
                  </FormGroup>
                </MobileEditCardFields>
              )}
            </MobileEditCard>
          );
        })}
        <MobileEditBottomBar>
          {canDelete && (
            <RowMenuWrapper>
              <KebabButton
                type="button"
                aria-label="Ações em lote"
                onClick={(e: React.MouseEvent<HTMLButtonElement>) => handleKebabClick(e, -1)}
              >
                <KebabIcon />
              </KebabButton>
              {openMenuId === -1 &&
                menuPos &&
                ReactDOM.createPortal(
                  <PortalRowMenu ref={portalMenuRef} style={menuPos}>
                    <RowMenuItem
                      danger
                      onClick={() => {
                        closeMenu();
                        onBulkExcluir(tarefas.filter(t => selectedIds.has(t.numero)));
                      }}
                    >
                      Excluir selecionados
                    </RowMenuItem>
                  </PortalRowMenu>,
                  document.body,
                )}
            </RowMenuWrapper>
          )}
          <EditSaveButton type="button" onClick={handleSave} disabled={isSaving}>
            {isSaving ? 'Salvando...' : 'Aplicar alterações'}
          </EditSaveButton>
        </MobileEditBottomBar>
      </MobileEditWrapper>
    </ActivityListWrapper>
  );
};
